import DOMPurify, { type Config } from "dompurify";

/**
 * Baseline allowlist applied to every sanitize call.
 *
 * DOMPurify's default tag allowlist has no `iframe`, and `iframe` is a member
 * of its DEFAULT_FORBID_CONTENTS set - so an un-configured sanitize deletes the
 * element *and everything inside it*, which is why embeds pasted into the
 * Umbraco rich text editor used to vanish without a trace. Umbraco itself
 * permits them (`ValidElements` contains `iframe[*]`), so the allowlist here is
 * what decides whether they survive.
 *
 * Which *origins* may actually be framed is not decided here - that is the CSP
 * `frame-src` directive in nginx-security-headers.conf. A host missing from it
 * renders as a blank box with a console violation, even though the tag is kept.
 *
 * `src`, `width`, `height`, `title` and `loading` are already default-allowed,
 * as are `data-*` attributes (so the `data-embed-*` on Umbraco's
 * `umb-embed-holder` oEmbed wrapper survive untouched).
 */
const BASE_ADD_TAGS = ["iframe"];

const BASE_ADD_ATTR = [
  "allow",
  "allowfullscreen",
  "frameborder",
  "scrolling",
  "referrerpolicy",
];

// `srcdoc` iframes inherit the parent origin and would sidestep `frame-src`
// entirely. It is not default-allowed, but forbid it explicitly rather than
// leaning on that.
const BASE_FORBID_ATTR = ["srcdoc"];

const BASE_CONFIG: Config = {
  ADD_TAGS: BASE_ADD_TAGS,
  ADD_ATTR: BASE_ADD_ATTR,
  FORBID_ATTR: BASE_FORBID_ATTR,
};

function mergeConfig(config?: Config): Config {
  if (!config) return BASE_CONFIG;

  const merged: Config = { ...BASE_CONFIG, ...config };

  // Concatenate the allowlists so a caller widening one cannot drop the
  // baseline. DOMPurify also accepts a predicate for ADD_TAGS/ADD_ATTR, which
  // has no meaningful union with an array - there, the caller's value stands
  // alone and it is on them to re-admit iframes.
  if (Array.isArray(config.ADD_TAGS)) {
    merged.ADD_TAGS = [...BASE_ADD_TAGS, ...config.ADD_TAGS];
  }
  if (Array.isArray(config.ADD_ATTR)) {
    merged.ADD_ATTR = [...BASE_ADD_ATTR, ...config.ADD_ATTR];
  }
  if (config.FORBID_ATTR) {
    merged.FORBID_ATTR = [...BASE_FORBID_ATTR, ...config.FORBID_ATTR];
  }

  return merged;
}

/**
 * Sanitize CMS-provided HTML before passing to dangerouslySetInnerHTML.
 * Single chokepoint for all Umbraco rich-text rendering.
 *
 * The optional `config` lets callers tighten or widen the allowlist (e.g.
 * permit extra inline tags) while still flowing through this entry point. It is
 * merged over BASE_CONFIG rather than replacing it, so widening the allowlist
 * for one block cannot silently re-break iframes everywhere else.
 */
export function sanitizeHtml(
  input: string | null | undefined,
  config?: Config
): string {
  if (!input) return "";
  return DOMPurify.sanitize(input, mergeConfig(config));
}
